import { Alert, Button, Spinner } from '@recipes/ui';
import { ExternalLink, KeyRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isNativeApp } from '@/lib/platform';
import { usePublicConfig } from '@/lib/hooks/use-public-config';

export interface IdentityInfo {
  issuer: string;
  accountUrl: string;
  startUrl: string;
}

export interface AuthModeInfo {
  /** `undefined` while the configuration is still loading. */
  mode: 'local' | 'oidc' | undefined;
  identity: IdentityInfo | null;
}

/**
 * Who signs users in (docs/partial/identity-service-spec.md 9): this app's own endpoints, or the
 * central identity service. Older servers answer without `auth` – that is `local`.
 */
export function useAuthMode(): AuthModeInfo {
  const config = usePublicConfig();
  if (config.data) {
    const auth = config.data.auth;
    return { mode: auth?.mode ?? 'local', identity: auth?.identity ?? null };
  }
  return { mode: config.isPending ? undefined : 'local', identity: null };
}

/** Where a sign-in through the identity service starts; the API sends the browser on. */
export function identityStartUrl(identity: IdentityInfo, returnTo: string): string {
  const params = new URLSearchParams({ returnTo });
  if (isNativeApp()) params.set('platform', 'native');
  return `${identity.startUrl}?${params.toString()}`;
}

export function startIdentitySignIn(identity: IdentityInfo, returnTo: string): void {
  window.location.assign(identityStartUrl(identity, returnTo));
}

/** The one button of the sign-in and registration pages under AUTH_MODE=oidc. */
export function IdentitySignInButton({
  identity,
  returnTo,
  label,
  testId = 'identity-sign-in',
}: {
  identity: IdentityInfo;
  returnTo: string;
  label?: string;
  testId?: string;
}) {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      className="w-full"
      onClick={() => startIdentitySignIn(identity, returnTo)}
      data-testid={testId}
    >
      <KeyRound aria-hidden /> {label ?? t('auth.identity.signIn')}
    </Button>
  );
}

/** A link to the account page of the identity service, where name, address, password and devices live. */
export function IdentityAccountLink({
  identity,
  path = '',
  label,
  testId = 'identity-account-link',
}: {
  identity: IdentityInfo;
  path?: string;
  label?: string;
  testId?: string;
}) {
  const { t } = useTranslation();
  return (
    <Button asChild variant="outline">
      <a href={`${identity.accountUrl}${path}`} data-testid={testId}>
        <ExternalLink aria-hidden /> {label ?? t('auth.identity.openAccount')}
      </a>
    </Button>
  );
}

const KNOWN_ERRORS = [
  'oidc_state',
  'oidc_code',
  'oidc_tokens',
  'oidc_failed',
  'access_denied',
  'email_conflict',
];

/** What the API said when a sign-in through the identity service did not end well (`?error=`). */
export function IdentityErrorAlert({ error }: { error: string | null }) {
  const { t } = useTranslation();
  if (!error) return null;
  const key = KNOWN_ERRORS.includes(error) ? error : 'oidc_failed';
  return (
    <Alert variant="destructive" data-testid="identity-error">
      {t(`auth.identity.errors.${key}`)}
    </Alert>
  );
}

export function AuthModeLoading() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center" data-testid="auth-mode-loading">
      <Spinner className="text-muted-foreground" />
    </div>
  );
}
